import { Field, Formik } from 'formik';
import { Platform, StyleSheet, View } from 'react-native';

import { Button, InlineAlert, NumberMaskInput } from '@fhs/ui';
import { giftCardNumberMask, giftCardPinMask } from '@fhs/ui/src/components/form-text-inputs/masks';

import { GiftCardFormInputErrors, GiftCardFormInputProps, StepAddCardFormProps } from './types';

const validateForm = (values: GiftCardFormInputProps) => {
  const errors: GiftCardFormInputErrors = {};
  if (!values.cardId || values.cardId.length < 16) {
    errors.cardId = 'Please enter a valid Card ID.';
  }

  if (!values.pin || values.pin.length < 4) {
    errors.pin = 'Please enter a valid Pin.';
  }

  return errors;
};

export const StepAddCardForm = ({
  cardId,
  onCardIdChange,
  pin,
  onPinChange,
  isLoadingGiftCard = false,
  onContinue,
  errorMessage,
}: StepAddCardFormProps) => {
  return (
    <Formik validate={validateForm} initialValues={{ cardId, pin }} onSubmit={onContinue}>
      {({ isSubmitting, submitForm }) => (
        <>
          <View style={styles.inputContainer}>
            <Field name="cardId">
              {({ field, form, meta }) => {
                return (
                  <NumberMaskInput
                    value={field.value ?? cardId}
                    onChange={newId => {
                      form.setFieldValue('cardId', newId);
                      form.setSubmitting(false);
                      onCardIdChange(newId);
                    }}
                    onBlur={form.handleBlur('cardId')}
                    error={meta.touched && meta.error}
                    style={styles.input}
                    label="Card ID"
                    placeholder="Enter the Card ID"
                    mask={giftCardNumberMask}
                  />
                );
              }}
            </Field>
          </View>
          <View style={styles.inputContainer}>
            <Field name="pin">
              {({ field, form, meta }) => {
                return (
                  <NumberMaskInput
                    value={field.value ?? pin}
                    onChange={newPin => {
                      form.setFieldValue('pin', newPin);
                      form.setSubmitting(false);
                      onPinChange(newPin);
                    }}
                    onBlur={form.handleBlur('pin')}
                    error={meta.touched && meta.error}
                    style={styles.input}
                    label="PIN"
                    placeholder="Enter the 4-digits PIN"
                    mask={giftCardPinMask}
                  />
                );
              }}
            </Field>
          </View>
          <InlineAlert
            type="info"
            message="You can find your Card ID and the PIN on the back of your physical gift card."
          />
          {errorMessage && (
            <View style={styles.errorAlert}>
              <InlineAlert
                type="error"
                message={errorMessage}
              />
            </View>
          )}
          <Button
            style={styles.continueButton}
            size="lg"
            loading={(isLoadingGiftCard || isSubmitting) && !errorMessage}
            disabled={isLoadingGiftCard || isSubmitting || !!errorMessage}
            onPress={submitForm}
          >
            <Button.Text>Continue</Button.Text>
          </Button>
        </>
      )}
    </Formik>
  );
};

const styles = StyleSheet.create({
  input: {
    maxHeight: 48,
    width: '100%',
    ...Platform.select({
      web: {
        flex: 1,
      },
      default: {
        flex: 0,
      },
    }),
  },
  inputContainer: {
    marginBottom: 16,
    gap: 4,
  },
  errorAlert: {
    marginTop: 12,
  },
  continueButton: {
    marginTop: 16,
  },
});
